// WHICH SAVE'S ANSWER IS STILL TRUE, AND WHOSE WRITE LANDS LAST.
//
// A quote has two things that save it -- the debounced autosave and the Save
// button -- and they write the same two sinks: the draft id, and the panel's
// shipments. Both are `await`ed, so between sending a request and reading its
// answer the screen can have moved on. Without an order, whichever response
// happens to arrive last wins, and that is not the same as the newest one
// winning.
//
// The damage is not hypothetical. A save in flight when a shipment is added
// returns a shipments list that PREDATES the add. Applying it drops the new
// shipment from the panel, and MaterialTable's reconcile then correctly reads
// a line still naming it as dangling and clears the assignment -- moments
// after a save that reported success, with the backend holding the line
// assigned. Reproduced in a browser against live.
//
// ⚠️ ORDERING RESPONSES IS NOT ENOUGH, WHICH IS WHY THERE IS A MUTEX TOO.
// The generation rule below decides which ANSWER may be applied; it says
// nothing about how many WRITES are in flight, and both defects that remain
// are about the writes:
//
//   * Postgres refuses the overlap outright -- "could not serialize access
//     due to concurrent update" -- and the user's explicit Save is the press
//     that fails, for no reason visible on screen. Loud and recoverable.
//   * The silent half: every draft tick sends `is_draft: true`. An autosave
//     in flight when a manual Save FINALISES the quote can commit last, and
//     the numbered quote quietly becomes a draft again. The generation rule
//     drops that stale answer, but the write already landed -- discarding a
//     response never unwrote anything.
//
// So the fix is ORDERING THE WRITES, not handling the error: a manual save
// waits for an in-flight autosave and goes last, and no autosave may start
// while a manual press is waiting or out. See `whenIdle` below.
//
// ⚠️ THE GENERATION HALF IGNORES A SUPERSEDED RESPONSE. IT DOES NOT CANCEL A
// REQUEST, and the difference is the entire design. An AbortController stops
// us *reading* an answer, but the request has already reached Odoo and
// committed -- the write happened whether or not anyone listens. So that rule
// has to be about which answer may be APPLIED, not about which request
// survives. The mutex is the part that governs requests.
//
// ONE MODULE RATHER THAN A COUNTER IN EACH CALLER. The rule was first written
// inline in both save paths, which is two copies of an ordering invariant that
// only works while they agree. Here it is one object with one definition, and
// the tests drive it without React, a page or a timer.

/** Which save path is asking. The two are governed differently on purpose --
 *  see `begin`. */
export type SaveKind = "auto" | "manual";

/** How long `whenIdle` waits before giving up and letting the manual save
 *  proceed anyway. A BACKSTOP, not a normal path: see the deadlock note on
 *  `whenIdle`. Comfortably longer than api.ts's 15s request abort, so a save
 *  that is merely slow is always waited out rather than cut short. */
export const IDLE_WAIT_CAP_MS = 16000;

/** One save's place in the order. Handed out by `begin`. */
export interface SaveTicket {
  /** True once a LATER save has begun. Check after every await, before
   *  writing anything the newer save also writes. */
  superseded(): boolean;
  /** Release the create slot and the write slot. Call in `finally` -- a
   *  rejected request that never released them would block every later create
   *  AND strand anyone waiting in `whenIdle` for the life of the page. */
  done(): void;
}

export interface SaveLane {
  /** Take a place in the order, or refuse.
   *
   *  Returns null in two cases, both meaning "do not send this request at
   *  all", which is different from sending it and discarding the answer:
   *
   *  1. `willCreate` is true and a create is already in flight -- see the
   *     note on createInFlight below.
   *  2. `kind` is "auto" and any write is in flight, or a manual press is
   *     waiting for its turn.
   *
   *  ⚠️ A REFUSED AUTO TICK MUST BE RE-ARMED BY ITS CALLER, not dropped. See
   *  the note on the auto rule inside. */
  begin(kind: SaveKind, willCreate: boolean): SaveTicket | null;

  /** Resolves once no write is in flight. Awaited by the MANUAL path only,
   *  before it takes its ticket, so the press goes last and alone.
   *
   *  While anyone is waiting here, auto ticks are refused -- otherwise a timer
   *  firing during the wait would take the slot the press was queueing for,
   *  and a 1.5s debounce against a slow link can do that indefinitely. */
  whenIdle(): Promise<void>;
}

export function createSaveLane(idleWaitCapMs: number = IDLE_WAIT_CAP_MS): SaveLane {
  // Monotonic, shared by both save paths. Two counters would each be
  // internally consistent and still let one path's stale answer land on top
  // of the other's fresh one, which is the bug rather than a variant of it.
  let generation = 0;

  // ⚠️ THE GENERATION GUARD CANNOT PREVENT A DOUBLE CREATE, which is why this
  // second piece of state exists. By the time a response is checked, both
  // requests have been sent and the backend has already made two records.
  // Discarding the loser's answer hides the orphan; it does not stop it, and
  // the orphan holds a full copy of the same lines.
  //
  // A save with no id CREATES. Two of those in flight together make two
  // quotes, and only one is ever referenced again. So a create may be in
  // flight only once.
  //
  // KEPT SEPARATE FROM THE WRITE COUNTER BELOW, even though a create in
  // flight now also implies a write in flight. They are different rules with
  // different scopes -- this one refuses a second CREATE from any caller,
  // the counter refuses ANY auto tick -- and folding them together would make
  // the double-create guard silently depend on the mutex keeping the exact
  // shape it has today.
  let createInFlight = false;

  // Writes currently out, both paths counted. The mutex is over this.
  let inFlight = 0;

  // Manual presses parked in `whenIdle`. Non-zero means a press has already
  // queued, and no auto tick may jump the queue ahead of it.
  let manualWaiting = 0;

  // Callbacks parked in `whenIdle`, run when `inFlight` reaches zero.
  let idleWaiters: Array<() => void> = [];

  function releaseIdleWaiters() {
    if (inFlight !== 0) return;
    // Swapped out before running: a waiter that starts another save must not
    // be walking the array it is being removed from.
    const waiting = idleWaiters;
    idleWaiters = [];
    waiting.forEach((w) => w());
  }

  return {
    begin(kind: SaveKind, willCreate: boolean): SaveTicket | null {
      if (willCreate && createInFlight) return null;

      // ⚠️ AUTO TICKS ARE DEFERRED, NEVER DROPPED, and that distinction is the
      // whole safety of this rule. Refusing a tick is only sound because the
      // caller re-arms its debounce, so the edit this tick would have carried
      // saves on the next one. A caller that returned on null WITHOUT
      // re-arming would silently lose that edit until something else happened
      // to change a dependency -- worse than the collision being prevented.
      // QuoteDetail's `saveRetryTick` is that re-arm, and it has its own pin.
      //
      // MANUAL IS NEVER REFUSED HERE. It has already waited its turn in
      // `whenIdle`; refusing the user's explicit press on top of that would
      // strand the button with nothing left to resolve it.
      if (kind === "auto" && (inFlight > 0 || manualWaiting > 0)) return null;

      const mine = ++generation;
      if (willCreate) createInFlight = true;
      inFlight += 1;
      let released = false;

      return {
        superseded: () => mine !== generation,
        done: () => {
          // Idempotent: a caller that releases twice must not free a slot
          // taken by the NEXT create, and must not drive `inFlight` negative
          // -- a negative count never equals zero, so it would wedge every
          // later `whenIdle` permanently.
          if (released) return;
          released = true;
          if (willCreate) createInFlight = false;
          inFlight -= 1;
          releaseIdleWaiters();
        },
      };
    },

    whenIdle(): Promise<void> {
      // Nothing out: the press proceeds immediately, and nothing can slip in
      // between this resolve and the caller's `begin` -- the caller resumes on
      // a microtask, while an autosave tick arrives on a timer, which is a
      // macrotask and therefore strictly later.
      if (inFlight === 0) return Promise.resolve();

      manualWaiting += 1;
      return new Promise<void>((resolve) => {
        let settled = false;
        let cap: ReturnType<typeof setTimeout>;

        const finish = () => {
          if (settled) return;
          settled = true;
          clearTimeout(cap);
          manualWaiting -= 1;
          resolve();
        };

        // ⚠️ NOTHING MAY WAIT HERE FOREVER. Three things have to fail before
        // the normal release is missed: both callers release in a `finally`,
        // every request is bounded by api.ts's 15s abort, and `done` is
        // idempotent so a double release cannot corrupt the count. If it is
        // missed anyway, this cap gives up waiting and lets the press go --
        // degrading to the pre-mutex behaviour, where the collision is at
        // worst loud and recoverable. A Save button that never resolves is
        // not recoverable, so the wait is bounded rather than perfect.
        cap = setTimeout(() => {
          idleWaiters = idleWaiters.filter((w) => w !== finish);
          finish();
        }, idleWaitCapMs);

        idleWaiters.push(finish);
      });
    },
  };
}
